import React, { Component } from "react";
import { Route, Switch, Redirect, withRouter } from "react-router-dom";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import { CookieMessage, themeFromSiteConfig } from "@americanbible/uikit";
import { I18n } from "@aws-amplify/core";
import styled, { ThemeProvider } from "styled-components";

import { fetchSiteConfig, fetchSiteLanguages } from "../../api/SiteApi";
import AuthPages from "../Auth/AuthPages";
import UserSettingsPage from "../Auth/UserSettingsPage";
import Bible from "../Bible/BibleContainer";
import BibleInfo from "../BibleInfo/BibleInfo";
import SiteInfo from "../SiteInfo/SiteInfo";
import PrivacyPage from "../PrivacyPage/PrivacyPage";
import SiteWrapper from "./SiteWrapper";
import Config from "../../config";
import redirectTo404 from "../../utils/redirectTo404";
import { addCustomGAId, setGADomain } from "../../utils/withTracker";
import { isCordovaApp } from "../../utils/cordovaUtils";
import { setSiteDictionary } from "../../utils/i18n";
import { bibleSource } from "../../storage";
import LegacyChapterRedirector from "../LegacyRedirector/LegacyChapterRedirector";
import AsyncComponent from "../AsyncComponent";

const AsyncWidgetBuilder = AsyncComponent(() =>
  import("../WidgetBuilder/WidgetBuilder")
);

const CustomCookieMessage = styled(CookieMessage)`
  z-index: 1;
`;

class SiteContainer extends Component {
  static propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired,
    hostname: PropTypes.string // passed in via context
  };

  static defaultProps = {
    hostname: document.location
  };

  state = {};

  handleSiteUpdate(site) {
    // TODO: this isn't enough to trigger a re-render of the BibleDropDown.BibleList
    this.setState({ site });
  }

  componentDidMount = () => {
    let { hostname } = this.props;

    if (hostname) {
      fetchSiteConfig(hostname)
        .then(site => {
          /* add GA ids from site as array here */
          // addCustomGAId(["UA-********-*"]);
          if (site.subdomain) {
            setGADomain(site.subdomain);
          }

          if (site.googleAnalyticsId) {
            addCustomGAId(site.googleAnalyticsId.split(","));
          }

          this.setState({ site });

          return Promise.all([fetchSiteLanguages(site), bibleSource()]);
        })
        .then(([languages, siteSource]) => {
          if (languages) setSiteDictionary(languages);

          if (!siteSource) {
            console.debug("unable to set up siteSource");
          } else {
            siteSource.setSiteUpdatedCallback(this.handleSiteUpdate.bind(this));
            this.setState({ siteSource }); // setSiteDictionary requires this setState to rerender UI
          }

          this.forceUpdate();
        })
        .catch(e => {
          console.error(e);
          this.setState({ siteNotFound: true });
        });
    }
  };

  render() {
    if (this.state.siteNotFound && !process.env.REACT_APP_NO_REDIRECT) {
      redirectTo404();
    }
    if (!this.state.site) {
      return null;
    }
    const defaultBible = this.state.site.bibles[0].id;
    const { site } = this.state;

    return (
      <SiteWrapper site={site}>
        <Helmet>
          <title>{site.title}</title>
          <meta name="theme-color" content={site.primaryColor} />
          <meta name="og:type" content="website" />
          <meta name="og:site_name" content={site.title} />
          <meta name="application-name" content={site.title} />
          <meta name="msapplication-TileColor" content={site.primaryColor} />
          <link
            href="https://fonts.googleapis.com/css?family=Noto+Sans:400,400i|Noto+Serif:400,400i"
            rel="stylesheet"
          />
          <link
            rel="mask-icon"
            href="/safari-pinned-tab.svg"
            color={site.primaryColor}
          />
        </Helmet>

        <Switch>
          <Route path="/bible/:bibleId/info" render={() => <BibleInfo />} />
          <Route
            path="/bible/:bibleId/:chapterId"
            render={() => <Bible site={site} />}
          />
          <Route path="/info" render={() => <SiteInfo site={site} />} />
          <Route path="/widget" component={AsyncWidgetBuilder} />
          <Route
            path="/privacy"
            render={() => <PrivacyPage contentType="privacy" site={site} />}
          />
          <Route
            path="/terms"
            render={() => <PrivacyPage contentType="terms" site={site} />}
          />
          {site.enabledUserAccounts && (
            <Route path="/user-settings" component={UserSettingsPage} />
          )}
          {site.enabledUserAccounts && (
            <Route path="/auth" component={AuthPages} />
          )}
          <Redirect
            exact
            from="/"
            to={`/bible/${defaultBible}/${Config.defaultChapter}`}
          />

          <Route
            path="/search/:queryString"
            render={({ history, match }) => {
              const urlQueryParams = history.location.search;
              const { queryString } = match.params;

              return (
                <Redirect
                  to={`/bible/${defaultBible}/search?q=${queryString}${
                    urlQueryParams ? "&" + urlQueryParams : ""
                  }`}
                />
              );
            }}
          />
          <Route
            path="/:legacyBible(\w\w\w-\w+)/:legacyBook(\w+)/:chapterNumber"
            component={LegacyChapterRedirector}
          />
        </Switch>
        <Route
          render={({ location: { pathname } }) => {
            // don't display on cordova apps
            if (isCordovaApp) return null;
            // don't display on T&C and privacy pages
            if (pathname === "/terms" || pathname === "/privacy") return null;

            return (
              <ThemeProvider theme={themeFromSiteConfig(site)}>
                <CustomCookieMessage>
                  {I18n.get(
                    "This website uses cookies and other tracking technologies to ensure you get the best experience on our website. For more information see our"
                  )}{" "}
                  <a href="/privacy" target="_blank">
                    {I18n.get("privacy policy")}
                  </a>
                  .
                </CustomCookieMessage>
              </ThemeProvider>
            );
          }}
        />
      </SiteWrapper>
    );
  }
}

export default withRouter(SiteContainer);
